<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import { billingModeLabel, employeeTypeLabel } from '../utils/format'
import type { Employee, Option } from '../types/hr'

const { t } = useI18n()
const auth = useAuthStore()
const { listEmployees, deleteEmployee, listWorkplaceOptions } = useHrApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canCreate = computed(() => permissions.value.includes('employees.create'))
const canUpdate = computed(() => permissions.value.includes('employees.update'))
const canDelete = computed(() => permissions.value.includes('employees.delete'))

const rows = ref<Employee[]>([])
const departments = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')

const filters = ref({
  search: '',
  department_agent_id: '' as string | number,
  employee_type: '',
  is_active: '1' as string | number,
})

const page = ref(1)
const totalPages = ref(1)
const total = ref(0)
const activeTotal = ref(0)

let searchTimer: ReturnType<typeof setTimeout> | undefined

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listEmployees({
      search: filters.value.search.trim() || undefined,
      department_agent_id: filters.value.department_agent_id || undefined,
      employee_type: filters.value.employee_type || undefined,
      is_active: filters.value.is_active,
      page: page.value,
      per_page: 20,
    })
    rows.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadSummary() {
  try {
    const active = await listEmployees({ is_active: 1, page: 1, per_page: 1 })
    activeTotal.value = active.meta.total
  } catch {
    activeTotal.value = 0
  }
}

async function loadDepartments() {
  try {
    departments.value = await listWorkplaceOptions()
  } catch {
    departments.value = []
  }
}

onMounted(() => {
  void loadDepartments()
  void loadSummary()
  void load()
})

watch(
  () => [filters.value.department_agent_id, filters.value.employee_type, filters.value.is_active],
  () => {
    page.value = 1
    void load()
  },
)

watch(
  () => filters.value.search,
  () => {
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
      page.value = 1
      void load()
    }, 300)
  },
)

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  void load()
}

async function remove(employee: Employee) {
  actionError.value = ''
  if (!window.confirm(t('hr.employees.deleteConfirm'))) return
  try {
    await deleteEmployee(employee.id)
    if (rows.value.length === 1 && page.value > 1) page.value -= 1
    await load()
    await loadSummary()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  }
}

function employeeName(employee: Employee): string {
  const name = `${employee.last_name} ${employee.first_name}`.trim()
  return name || employee.employee_code
}
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('hr.employees.title') }}</h2>
  </div>

  <div class="toolbar">
    <input
      v-model="filters.search"
      class="search-input"
      type="search"
      :placeholder="t('hr.common.searchPlaceholder')"
    >
    <select v-model="filters.department_agent_id" class="filter-select">
      <option value="">{{ t('hr.common.allDepartments') }}</option>
      <option v-for="department in departments" :key="department.id" :value="department.id">
        {{ department.name }}
      </option>
    </select>
    <select v-model="filters.employee_type" class="filter-select">
      <option value="">{{ t('hr.common.allTypes') }}</option>
      <option value="office">{{ t('hr.employeeType.office') }}</option>
      <option value="production">{{ t('hr.employeeType.production') }}</option>
      <option value="mixed">{{ t('hr.employeeType.mixed') }}</option>
    </select>
    <select v-model="filters.is_active" class="filter-select">
      <option value="1">{{ t('hr.common.active') }}</option>
      <option value="0">{{ t('hr.common.inactive') }}</option>
      <option value="">{{ t('hr.common.all') }}</option>
    </select>
    <span class="spacer" />
    <div class="summary">
      <span>{{ t('hr.employees.totals', { total, active: activeTotal }) }}</span>
    </div>
    <RouterLink v-if="canCreate" class="btn-primary" to="/employees/new">
      {{ t('hr.employees.newEmployee') }}
    </RouterLink>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('hr.employees.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
  </div>

  <div v-else-if="rows.length === 0" class="state-message"><p>{{ t('hr.employees.empty') }}</p></div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.employees.name') }}</th>
            <th>{{ t('hr.employees.code') }}</th>
            <th>{{ t('hr.employees.department') }}</th>
            <th>{{ t('hr.employees.jobTitle') }}</th>
            <th>{{ t('hr.employees.type') }}</th>
            <th>{{ t('hr.employees.billing') }}</th>
            <th>{{ t('hr.employees.hireDate') }}</th>
            <th>{{ t('hr.employees.status') }}</th>
            <th class="cell-actions">{{ t('hr.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="employee in rows" :key="employee.id">
            <td class="cell-name">{{ employeeName(employee) }}</td>
            <td class="cell-code">{{ employee.employee_code }}</td>
            <td>{{ employee.department_name || '—' }}</td>
            <td>{{ employee.job_title || '—' }}</td>
            <td>{{ employeeTypeLabel(t, employee.employee_type) }}</td>
            <td>{{ billingModeLabel(t, employee.billing_mode) }}</td>
            <td class="cell-date">{{ formatDisplayDate(employee.hire_date) || '—' }}</td>
            <td>
              <span :class="['status-badge', employee.is_active === 1 ? 'status-ok' : 'status-neutral']">
                {{ employee.is_active === 1 ? t('hr.common.active') : t('hr.common.inactive') }}
              </span>
            </td>
            <td class="cell-actions">
              <RouterLink class="link-action" :to="`/employees/${employee.id}`">
                {{ t('hr.employees.view') }}
              </RouterLink>
              <template v-if="canUpdate">
                &nbsp;·&nbsp;
                <RouterLink class="link-action" :to="`/employees/${employee.id}/edit`">
                  {{ t('hr.employees.edit') }}
                </RouterLink>
              </template>
              <template v-if="canDelete">
                &nbsp;·&nbsp;
                <button class="link-action danger" type="button" @click="remove(employee)">
                  {{ t('hr.employees.delete') }}
                </button>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ t('hr.common.page', { page, total: Math.max(totalPages, 1) }) }}</span>
      <button type="button" :disabled="page <= 1" @click="goToPage(page - 1)">
        {{ t('hr.common.previous') }}
      </button>
      <button type="button" :disabled="page >= totalPages" @click="goToPage(page + 1)">
        {{ t('hr.common.next') }}
      </button>
    </div>
  </template>
</template>
